import { Injectable, inject } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import {
  ApiError,
  AppError,
  getErrorPrefix,
  getErrorMessage,
} from '../../shared/model/error.interface';
import { TokenStorageService } from '../auth/token-storage.service';

/**
 * Servicio centralizado para la gestión y normalización de errores en la aplicación.
 * * Este servicio actúa como un procesador unificado que captura fallos tanto de red (HTTP)
 * como de ejecución lógica. Su propósito es transformar respuestas crudas del servidor
 * en una estructura de error coherente (`AppError`), gestionar registros de depuración
 * y ejecutar acciones automáticas de navegación ante errores críticos (ej. redirección al login).
 */
@Injectable({
  providedIn: 'root',
})
export class ErrorHandlerService {
  /** Servicio de navegación inyectado para gestionar redirecciones ante errores de acceso. */
  private readonly router = inject(Router);
  /** Servicio de almacenamiento inyectado para la limpieza de credenciales en errores de sesión. */
  private readonly tokenStorage = inject(TokenStorageService);

  /** * Listado de códigos de error específicos que indican una sesión corrupta o expirada
   * y que requieren que el usuario vuelva a autenticarse.
   */
  private readonly authRedirectCodes = ['AUTH_003', 'AUTH_007', 'AUTH_008'];

  /**
   * Punto de entrada principal para el procesamiento de fallos en peticiones HTTP.
   * * Realiza la orquestación del manejo de errores: primero transforma la respuesta,
   * luego registra el evento para auditoría técnica y finalmente evalúa si el error
   * requiere una acción de navegación inmediata.
   * * @param error La respuesta de error cruda emitida por el `HttpClient`.
   * @returns Un objeto `AppError` normalizado y listo para ser consumido por la interfaz de usuario.
   */
  handleHttpError(error: HttpErrorResponse): AppError {
    const appError = this.parseHttpError(error);
    this.logError(appError);
    this.handleSpecialCases(appError);
    return appError;
  }

  /**
   * Transforma una respuesta `HttpErrorResponse` en una estructura `AppError` amigable.
   * * Maneja casos críticos como la pérdida total de conexión (status 0) y delega la
   * extracción de detalles específicos del backend a métodos auxiliares.
   * * @param error La respuesta de error HTTP a procesar.
   * @returns Estructura de error estandarizada con código interno y mensaje localizado.
   */
  parseHttpError(error: HttpErrorResponse): AppError {
    // Si no hay conexión (status 0)
    if (error.status === 0) {
      return {
        errorCode: 'NETWORK',
        message: getErrorMessage('NETWORK'),
        httpStatus: 0,
        timestamp: new Date(),
        url: error.url || undefined,
      };
    }

    // Intentar parsear como ApiError del backend
    const apiError = this.extractApiError(error);

    return {
      errorCode: apiError.errorCode,
      message: getErrorMessage(apiError.errorCode, apiError.message),
      httpStatus: error.status,
      timestamp: new Date(),
      url: error.url || undefined,
    };
  }

  /**
   * Analiza el cuerpo de la respuesta HTTP para recuperar la estructura de error del backend.
   * * Si el cuerpo no contiene el formato `ApiError` esperado (por ejemplo, ante un fallo
   * de un proxy o balanceador), genera un error genérico basado en el estado HTTP.
   * * @param error La respuesta de error HTTP.
   * @returns Los detalles del error según la interfaz `ApiError`.
   */
  private extractApiError(error: HttpErrorResponse): ApiError {
    // Si el error tiene el formato esperado del backend
    if (error.error && typeof error.error === 'object' && error.error.errorCode) {
      return error.error as ApiError;
    }

    // Fallback para errores que no vienen del backend
    return {
      success: false,
      status: 'error',
      errorCode: this.getDefaultErrorCode(error.status),
      message: error.error?.message || error.message || 'Error desconocido',
    };
  }

  /**
   * Asocia estados HTTP estándar a códigos de error internos de la aplicación.
   * * Proporciona una red de seguridad para errores que no incluyen un código explícito
   * del servidor, permitiendo una clasificación básica por categorías (Validación, Auth, Servidor).
   * * @param status El código de estado HTTP recibido.
   * @returns Un código de error interno (ej. 'SRV_001' para errores 500).
   */
  private getDefaultErrorCode(status: number): string {
    switch (status) {
      case 400:
        return 'VAL_001';
      case 401:
        return 'AUTH_002';
      case 403:
        return 'PERM_001';
      case 404:
        return 'NOT_001';
      case 500:
      case 502:
      case 503:
      case 504:
        return 'SRV_001';
      default:
        return 'UNKNOWN';
    }
  }

  /**
   * Registra los detalles del error en la consola para facilitar el debugging.
   * * @param error El objeto de error normalizado a registrar.
   */
  private logError(error: AppError): void {
    console.error('[ErrorHandler]', {
      errorCode: error.errorCode,
      message: error.message,
      httpStatus: error.httpStatus,
      url: error.url,
      timestamp: error.timestamp,
    });
  }

  /**
   * Ejecuta efectos secundarios basados en la naturaleza del código de error.
   * * Gestiona flujos críticos de navegación: redirige al login si la sesión es inválida
   * o al inicio del panel si el usuario intenta acceder a recursos para los que no tiene permisos.
   * * @param error El error ya procesado para evaluar sus efectos.
   */
  private handleSpecialCases(error: AppError): void {
    const prefix = getErrorPrefix(error.errorCode);

    // Si es un error de autenticación que requiere re-login
    if (prefix === 'AUTH' && this.authRedirectCodes.includes(error.errorCode)) {
      this.tokenStorage.clear();
      this.router.navigate(['/login']);
      return;
    }

    // Si es un error de permisos, redirigir al inicio del panel
    if (prefix === 'PERM') {
      this.router.navigate(['/orden-general']);
    }
  }

  /**
   * Procesa errores genéricos de JavaScript que ocurren fuera de las peticiones HTTP.
   * * Útil para capturar excepciones de lógica de negocio o errores inesperados en el cliente.
   * * @param error La excepción capturada.
   * @returns Un `AppError` estructurado como error desconocido.
   */
  handleError(error: Error): AppError {
    const appError: AppError = {
      errorCode: 'UNKNOWN',
      message: error.message || getErrorMessage('UNKNOWN'),
      httpStatus: 0,
      timestamp: new Date(),
    };

    this.logError(appError);
    return appError;
  }

  /**
   * Determina si el error pertenece a la categoría de Autenticación.
   * @param error El error a verificar.
   * @returns `true` si el código de error tiene el prefijo 'AUTH'.
   */
  isAuthError(error: AppError): boolean {
    return getErrorPrefix(error.errorCode) === 'AUTH';
  }

  /**
   * Determina si el error pertenece a la categoría de Validación de datos.
   * @param error El error a verificar.
   * @returns `true` si el código de error tiene el prefijo 'VAL'.
   */
  isValidationError(error: AppError): boolean {
    return getErrorPrefix(error.errorCode) === 'VAL';
  }

  /**
   * Determina si el error pertenece a la categoría de Permisos o Autorización.
   * @param error El error a verificar.
   * @returns `true` si el código de error tiene el prefijo 'PERM'.
   */
  isPermissionError(error: AppError): boolean {
    return getErrorPrefix(error.errorCode) === 'PERM';
  }

  /**
   * Determina si el error indica que un recurso no fue encontrado.
   * @param error El error a verificar.
   * @returns `true` si el código de error tiene el prefijo 'NOT'.
   */
  isNotFoundError(error: AppError): boolean {
    return getErrorPrefix(error.errorCode) === 'NOT';
  }

  /**
   * Determina si el error se originó por un fallo crítico en el servidor.
   * @param error El error a verificar.
   * @returns `true` si el código de error tiene el prefijo 'SRV'.
   */
  isServerError(error: AppError): boolean {
    return getErrorPrefix(error.errorCode) === 'SRV';
  }
}
